import { useEffect } from 'react';

// Keeps a CSS custom property (default: --chat-vh) in sync with the mobile
// visual viewport. On iOS Safari the on-screen keyboard shrinks the visual
// viewport without changing the layout viewport, so a `100dvh` container ends
// up taller than the visible area — leaving an empty (dark) strip under the
// input and detaching it from the keyboard. Writing visualViewport.height into
// a CSS var lets the chat root track the truly visible height instead.
//
// `offsetTop` is also exposed as --chat-vv-offset because iOS shifts the visual
// viewport down when the keyboard opens; consumers can translate by it if they
// use a fixed-position root.
const useVisualViewport = (varName = '--chat-vh', offsetVarName = '--chat-vv-offset') => {
    useEffect(() => {
        const vv = window.visualViewport;
        const root = document.documentElement;

        const apply = () => {
            if (vv) {
                root.style.setProperty(varName, `${vv.height}px`);
                root.style.setProperty(offsetVarName, `${vv.offsetTop}px`);
            } else {
                root.style.setProperty(varName, '100dvh');
                root.style.setProperty(offsetVarName, '0px');
            }
        };

        apply();

        if (!vv) return undefined;

        vv.addEventListener('resize', apply);
        vv.addEventListener('scroll', apply);
        return () => {
            vv.removeEventListener('resize', apply);
            vv.removeEventListener('scroll', apply);
            root.style.removeProperty(varName);
            root.style.removeProperty(offsetVarName);
        };
    }, [varName, offsetVarName]);
};

export default useVisualViewport;
